/**
 * useFocusTrap — Modal focus trap + focus-restore.
 *
 * Sprint 10.3.A: closes one of the deferred a11y items from Phase 8.3.
 * Captures the element that was focused before the modal opened, traps
 * Tab cycling within the modal's DOM subtree while it's mounted, and
 * restores focus to the original element on unmount.
 *
 * Keep `useEscapeClose` (esc to close) and this hook decoupled — they're
 * separate concerns and not all modals want both behaviors. Modals that
 * already manage initial focus themselves (e.g. AddContextSheet auto-
 * focuses its textarea) should pass `initialFocus: 'none'`.
 */

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusables(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
}

interface UseFocusTrapOptions {
  enabled?: boolean;
  /**
   * `'first'` (default) focuses the first focusable element when the trap
   * mounts. `'none'` leaves initial focus alone — for modals that focus
   * their own primary input on open (e.g. textarea-led sheets).
   */
  initialFocus?: 'first' | 'none';
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  options: UseFocusTrapOptions = {},
) {
  const { enabled = true, initialFocus = 'first' } = options;

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = (document.activeElement as HTMLElement | null) ?? null;

    let focusTimer: number | undefined;
    if (initialFocus === 'first') {
      // Defer one tick so the modal's enter animation can mount the DOM
      // before we query for focusables.
      focusTimer = window.setTimeout(() => {
        getFocusables(container)[0]?.focus();
      }, 50);
    }

    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusables = getFocusables(container!);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const isInside = container!.contains(active);

      if (e.shiftKey) {
        if (active === first || !isInside) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !isInside) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);

    return () => {
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      // Restore focus to the previously-active element if it's still in the DOM.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [containerRef, enabled, initialFocus]);
}
